import { IconButton } from '@/components/ui/IconButton';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { ToastItem } from '@/contexts/ToastContext';

type Props = {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
};

const variantStyles: Record<ToastItem['variant'], string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  error: 'border-red-200 bg-red-50 text-red-900',
  info: 'border-[var(--fv-border)] bg-[var(--fv-surface-elevated)] text-[var(--fv-text)]'
};

export function ToastViewport({ toasts, onDismiss }: Props) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  if (!toasts.length) return null;

  return (
    <div
      className={cn(
        'pointer-events-none fixed top-4 z-[300] flex w-full max-w-sm flex-col gap-2',
        isRtl ? 'left-4 items-start' : 'right-4 items-end'
      )}
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className={cn(
            'pointer-events-auto flex w-full items-start gap-3 rounded-[var(--fv-radius)] border px-4 py-3 text-sm shadow-[var(--fv-shadow-md)]',
            variantStyles[toast.variant]
          )}
        >
          <p className="min-w-0 flex-1 leading-snug">{toast.message}</p>
          <IconButton
            size="sm"
            aria-label={t('toast.dismiss')}
            onClick={() => onDismiss(toast.id)}
            className="opacity-70 hover:opacity-100"
          >
            <X className="h-4 w-4" aria-hidden />
          </IconButton>
        </div>
      ))}
    </div>
  );
}
